import React, { useState, useEffect, Fragment, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { usePermissions } from '../context/PermissionsContext';

// ─── Permission display metadata ─────────────────────────────────────────────
// Maps permissionName → { section, group, label, description, note? }
const PERMISSION_META = {
  // ── Administration › User Management ────────────────────────────────────
  manage_users:        { section: 'Administration', group: 'User Management',    order: 1,  label: 'Manage Users',                description: 'Allows you to create, edit, and deactivate user accounts' },
  view_users:          { section: 'Administration', group: 'User Management',    order: 2,  label: 'View Users',                  description: 'Allows you to view the full user directory and their role assignments' },
  manage_roles:        { section: 'Administration', group: 'User Management',    order: 3,  label: 'Manage Roles',                description: 'Allows you to create and manage custom roles' },
  manage_permissions:  { section: 'Administration', group: 'User Management',    order: 4,  label: 'Manage Permissions',          description: 'Allows you to edit permission assignments for all roles' },
  // ── Administration › Company Settings ───────────────────────────────────
  manage_departments:      { section: 'Administration', group: 'Company Settings', order: 5,  label: 'Manage Departments',        description: 'Allows you to create, rename, and manage departments' },
  manage_finance_settings: { section: 'Administration', group: 'Company Settings', order: 6,  label: 'Finance Settings',          description: 'Allows you to configure GL accounts, financial years, currencies, exchange rates, mileage rates, and approval SLAs' },
  manage_company_settings: { section: 'Administration', group: 'Company Settings', order: 7,  label: 'Company Settings',          description: 'Allows you to manage company-level settings, SSO/authentication configuration, and finance system integrations' },
  manage_help:             { section: 'Administration', group: 'Company Settings', order: 8,  label: 'Manage Help Articles',      description: 'Allows you to create and edit help articles for your company' },
  manage_subscriptions:    { section: 'Administration', group: 'Company Settings', order: 8,  label: 'Manage Subscriptions',      description: 'Allows you to view and manage module subscriptions' },
  // ── Administration › Monitoring ─────────────────────────────────────────
  view_audit_logs: { section: 'Administration', group: 'Monitoring', order: 9, label: 'View Audit Logs', description: 'Allows you to view security events and the full activity audit trail' },

  // ── Operations › Invoices ────────────────────────────────────────────────
  view_invoices:   { section: 'Operations', group: 'Invoices', order: 10, label: 'View All Invoices',    description: 'Allows you to view all company invoices and their details' },
  create_invoices: { section: 'Operations', group: 'Invoices', order: 11, label: 'Create Invoices',      description: 'Allows you to create new invoice records' },
  edit_invoices:   { section: 'Operations', group: 'Invoices', order: 12, label: 'Edit Invoices',        description: 'Allows you to edit and update existing invoice details' },
  upload_invoices: { section: 'Operations', group: 'Invoices', order: 13, label: 'Upload Invoice Files', description: 'Allows you to upload and attach invoice documents' },
  delete_invoices: { section: 'Operations', group: 'Invoices', order: 14, label: 'Delete Invoices',      description: 'Allows you to permanently delete invoice records' },
  submit_invoices: { section: 'Operations', group: 'Invoices', order: 15, label: 'Submit for Approval',  description: 'Allows you to submit invoices into the approval workflow' },
  // These are department-scoped equivalents — assigned specifically to Department Managers
  'invoices.view':            { section: 'Operations', group: 'Department-Scoped Access', order: 16, label: 'View Invoices',           description: 'Allows you to view invoices scoped to your assigned department(s)',               note: 'Dept-scoped equivalent of "View All Invoices"' },
  'invoices.create':          { section: 'Operations', group: 'Department-Scoped Access', order: 17, label: 'Create Invoices',         description: 'Allows you to create invoices within your assigned department',                  note: 'Dept-scoped equivalent of "Create Invoices"' },
  'invoices.edit':            { section: 'Operations', group: 'Department-Scoped Access', order: 18, label: 'Edit Invoices',           description: 'Allows you to edit invoices within your assigned department',                    note: 'Dept-scoped equivalent of "Edit Invoices"' },
  'invoices.view.department': { section: 'Operations', group: 'Department-Scoped Access', order: 19, label: 'View Dept Invoices Only', description: 'Allows you to view invoices restricted to your assigned department(s) only — excludes company-wide invoices' },
  'vendors.view':             { section: 'Operations', group: 'Department-Scoped Access', order: 20, label: 'View Vendors',            description: 'Allows you to view vendor records — scoped to department-level access',          note: 'Dept-scoped equivalent of "View Vendors"' },
  'departments.view':         { section: 'Operations', group: 'Department-Scoped Access', order: 21, label: 'View Departments',        description: 'Allows you to view departments — scoped to department-level access',            note: 'Dept-scoped equivalent of "View Departments"' },
  // ── Operations › Expenses ────────────────────────────────────────────────
  view_my_expenses: { section: 'Operations', group: 'Expenses', order: 22, label: 'View Own Expenses', description: 'Allows you to view your own submitted expense claims' },
  submit_expenses:  { section: 'Operations', group: 'Expenses', order: 23, label: 'Submit Expenses',   description: 'Allows you to submit expense claims for reimbursement' },
  // ── Operations › Purchase Orders ─────────────────────────────────────────
  view_purchase_orders:   { section: 'Operations', group: 'Purchase Orders', order: 24, label: 'View Purchase Orders',   description: 'Allows you to view the purchase orders list and details' },
  create_purchase_orders: { section: 'Operations', group: 'Purchase Orders', order: 25, label: 'Create Purchase Orders', description: 'Allows you to create and draft new purchase orders' },
  // ── Operations › Vendors ─────────────────────────────────────────────────
  view_vendors:     { section: 'Operations', group: 'Vendors',     order: 26, label: 'View Vendors',      description: 'Allows you to view the full vendor directory and details' },
  // ── Operations › Departments ─────────────────────────────────────────────
  view_departments: { section: 'Operations', group: 'Departments', order: 27, label: 'View Departments', description: 'Allows you to view the department list and organisational structure' },
  // ── Operations › General ─────────────────────────────────────────────────
  view_dashboard:   { section: 'Operations', group: 'General',     order: 28, label: 'Access Dashboard',  description: 'Allows you to access the main finance dashboard' },

  // ── Management › Approvals ───────────────────────────────────────────────
  approve_invoices:          { section: 'Management', group: 'Approvals', order: 30, label: 'Approve Invoices',           description: 'Allows you to review, approve, or reject invoices in the approval queue' },
  approve_expenses:          { section: 'Management', group: 'Approvals', order: 31, label: 'Approve Expenses',           description: 'Allows you to review and approve or reject expense claims' },
  approve_vendors:           { section: 'Management', group: 'Approvals', order: 32, label: 'Approve Vendors',            description: 'Allows you to approve or reject vendor registration requests' },
  approve_purchase_orders:   { section: 'Management', group: 'Approvals', order: 33, label: 'Approve Purchase Orders',    description: 'Allows you to approve or reject purchase orders' },
  delegate_expense_approval: { section: 'Management', group: 'Approvals', order: 34, label: 'Delegate Expense Approvals', description: 'Allows you to delegate your expense approval authority to another user' },
  // ── Management › Full Management ─────────────────────────────────────────
  manage_expenses:          { section: 'Management', group: 'Full Management', order: 35, label: 'Manage All Expenses',      description: 'Allows you to fully manage, edit, and administer all company expense claims' },
  view_all_expenses:        { section: 'Management', group: 'Full Management', order: 36, label: 'View All Expenses',        description: 'Allows you to view all expense claims across the entire company' },
  process_expense_payments: { section: 'Management', group: 'Full Management', order: 37, label: 'Process Expense Payments', description: 'Allows you to record and process expense reimbursement payments' },
  manage_vendors:           { section: 'Management', group: 'Full Management', order: 38, label: 'Manage Vendors',           description: 'Allows you to add, edit, and deactivate vendor records' },
  manage_purchase_orders:   { section: 'Management', group: 'Full Management', order: 39, label: 'Manage Purchase Orders',   description: 'Allows you to fully manage purchase orders including closing and deletion' },
  // ── Management › Budgets & Reporting ─────────────────────────────────────
  view_budgets:                   { section: 'Management', group: 'Budgets & Reporting', order: 40, label: 'View Budgets',               description: 'Allows you to view budget allocations and spending against budget' },
  manage_budgets:                 { section: 'Management', group: 'Budgets & Reporting', order: 41, label: 'Manage Budgets',             description: 'Allows you to create, edit, and manage company budgets' },
  'budgets.view.my-departments':  { section: 'Management', group: 'Budgets & Reporting', order: 42, label: 'View Dept Budgets',          description: 'Allows you to view budgets for your assigned department(s)' },
  'budgets.manage.my-departments':{ section: 'Management', group: 'Budgets & Reporting', order: 43, label: 'Manage Dept Budgets',        description: 'Allows you to create and manage budgets for your assigned department(s)' },
  view_reports:                   { section: 'Management', group: 'Budgets & Reporting', order: 44, label: 'View Reports',               description: 'Allows you to access general reports and analytics' },
  view_finance_reports:           { section: 'Management', group: 'Budgets & Reporting', order: 45, label: 'View Finance Reports',       description: 'Allows you to view VAT claims and detailed financial reports' },
  view_expense_reports:           { section: 'Management', group: 'Budgets & Reporting', order: 46, label: 'View Expense Reports',       description: 'Allows you to view expense analytics and summary reports' },

  // ── Platform ─────────────────────────────────────────────────────────────
  manage_companies:   { section: 'Platform', group: 'Platform', order: 50, label: 'Manage Companies',    description: 'Allows you to create and manage company accounts on the platform' },
  manage_system:      { section: 'Platform', group: 'Platform', order: 51, label: 'System Settings',     description: 'Allows you to configure global system-level settings' },
  manage_global_help: { section: 'Platform', group: 'Platform', order: 52, label: 'Global Help Content', description: 'Allows you to create and edit help articles visible across all companies' },
};

// ─── Section visual config ────────────────────────────────────────────────────
const SECTION_CONFIG = {
  Administration: {
    label: 'Administration',
    description: 'System configuration, user management, and company settings',
    headerBg: 'bg-blue-50 border-t-2 border-blue-200',
    headerText: 'text-blue-800',
    headerMuted: 'text-blue-500',
    headerBadge: 'bg-blue-100 text-blue-700 border border-blue-200',
    headerChevron: 'text-blue-400',
    groupBg: 'bg-blue-50/60',
    groupBorder: 'border-blue-100',
    groupText: 'text-blue-700',
    rowHover: 'hover:bg-blue-50/40',
    checkColor: 'accent-blue-600',
  },
  Operations: {
    label: 'Operations',
    description: 'Day-to-day document creation, submission, and processing',
    headerBg: 'bg-emerald-50 border-t-2 border-emerald-200',
    headerText: 'text-emerald-800',
    headerMuted: 'text-emerald-500',
    headerBadge: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
    headerChevron: 'text-emerald-400',
    groupBg: 'bg-emerald-50/60',
    groupBorder: 'border-emerald-100',
    groupText: 'text-emerald-700',
    rowHover: 'hover:bg-emerald-50/40',
    checkColor: 'accent-emerald-600',
  },
  Management: {
    label: 'Management & Oversight',
    description: 'Approvals, budget control, and management reporting',
    headerBg: 'bg-purple-50 border-t-2 border-purple-200',
    headerText: 'text-purple-800',
    headerMuted: 'text-purple-500',
    headerBadge: 'bg-purple-100 text-purple-700 border border-purple-200',
    headerChevron: 'text-purple-400',
    groupBg: 'bg-purple-50/60',
    groupBorder: 'border-purple-100',
    groupText: 'text-purple-700',
    rowHover: 'hover:bg-purple-50/40',
    checkColor: 'accent-purple-600',
  },
  Platform: {
    label: 'Platform Administration',
    description: 'Reserved for platform owners only — cross-company system access',
    headerBg: 'bg-amber-50 border-t-2 border-amber-200',
    headerText: 'text-amber-800',
    headerMuted: 'text-amber-500',
    headerBadge: 'bg-amber-100 text-amber-700 border border-amber-200',
    headerChevron: 'text-amber-400',
    groupBg: 'bg-amber-50/60',
    groupBorder: 'border-amber-100',
    groupText: 'text-amber-700',
    rowHover: 'hover:bg-amber-50/40',
    checkColor: 'accent-amber-600',
  },
  Other: {
    label: 'Other Permissions',
    description: 'Additional permissions not yet categorised',
    headerBg: 'bg-gray-50 border-t-2 border-gray-200',
    headerText: 'text-gray-700',
    headerMuted: 'text-gray-400',
    headerBadge: 'bg-gray-100 text-gray-600 border border-gray-200',
    headerChevron: 'text-gray-400',
    groupBg: 'bg-gray-50',
    groupBorder: 'border-gray-100',
    groupText: 'text-gray-600',
    rowHover: 'hover:bg-gray-50',
    checkColor: 'accent-gray-600',
  },
};

// ─── Role visual config ───────────────────────────────────────────────────────
const ROLE_META = {
  'Admin':                { desc: 'Full company access',               badge: 'bg-blue-100 text-blue-800 border-blue-200',         accent: 'bg-blue-500' },
  'Finance Admin':        { desc: 'Finance & invoice admin',           badge: 'bg-emerald-100 text-emerald-800 border-emerald-200', accent: 'bg-emerald-500' },
  'Manager':              { desc: 'Dept manager with approval rights', badge: 'bg-orange-100 text-orange-800 border-orange-200',   accent: 'bg-orange-500' },
  'Department Manager':   { desc: 'Dept-level management',             badge: 'bg-orange-100 text-orange-800 border-orange-200',   accent: 'bg-orange-500' },
  'Departments Manager':  { desc: 'Multi-dept management',             badge: 'bg-orange-100 text-orange-800 border-orange-200',   accent: 'bg-orange-500' },
  'Department Approver':  { desc: 'Approves invoices/expenses',        badge: 'bg-amber-100 text-amber-800 border-amber-200',      accent: 'bg-amber-500' },
  'Finance User':         { desc: 'Finance view access',               badge: 'bg-teal-100 text-teal-800 border-teal-200',         accent: 'bg-teal-500' },
  'Employee':             { desc: 'Self-service only',                 badge: 'bg-slate-100 text-slate-700 border-slate-200',      accent: 'bg-slate-400' },
  'User':                 { desc: 'Basic user, minimum privileges',    badge: 'bg-gray-100 text-gray-700 border-gray-200',         accent: 'bg-gray-400' },
  'Viewer':               { desc: 'Read-only access',                  badge: 'bg-gray-100 text-gray-600 border-gray-200',         accent: 'bg-gray-300' },
};

const SYSTEM_ROLE_NAMES = new Set([
  'Admin', 'Finance Admin', 'Finance User',
  'Department Manager', 'Departments Manager', 'Department Approver',
  'Employee', 'Manager', 'Finance', 'User',
]);

export default function RolePermissions() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [mappings, setMappings] = useState({});
  const [changedMappings, setChangedMappings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [creatingRole, setCreatingRole] = useState(false);
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [targetCompanyId, setTargetCompanyId] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (hasPermission('manage_users')) {
      fetchPermissionsMatrix();
    }
  }, []);

  if (!hasPermission('manage_users')) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 font-semibold">Access Denied</p>
        <p className="text-gray-500 mt-2">You do not have permission to manage role permissions.</p>
      </div>
    );
  }

  const fetchPermissionsMatrix = async (overrideCompanyId) => {
    try {
      setLoading(true);
      const params = {};
      const companyParam = overrideCompanyId || targetCompanyId;
      if (companyParam) params.targetCompanyId = companyParam;
      const response = await apiClient.get('/roles/permissions-matrix', { params });
      setRoles(response.data.roles);
      setPermissions(response.data.permissions);
      setMappings(response.data.mappings);
      if (response.data.isPlatformOwner) {
        setIsPlatformOwner(true);
        setCompanies(response.data.companies || []);
        if (!targetCompanyId && !overrideCompanyId) {
          setTargetCompanyId(response.data.targetCompanyId);
        }
      }
    } catch (error) {
      console.error('Failed to fetch permissions matrix:', error);
      alert('Failed to load permissions matrix');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (newCompanyId) => {
    if (hasChanges && !window.confirm('You have unsaved changes. Switch company and discard them?')) {
      return;
    }
    setTargetCompanyId(newCompanyId);
    setChangedMappings({});
    setHasChanges(false);
    fetchPermissionsMatrix(newCompanyId);
  };

  const togglePermission = (roleId, permissionId) => {
    const key = `${roleId}_${permissionId}`;
    const newMappings = { ...mappings };
    const newChangedMappings = { ...changedMappings };

    if (newMappings[key]) {
      delete newMappings[key];
      newChangedMappings[key] = false;
    } else {
      newMappings[key] = true;
      newChangedMappings[key] = true;
    }

    setMappings(newMappings);
    setChangedMappings(newChangedMappings);
    setHasChanges(true);
  };

  const roleHasPermission = (roleId, permissionId) => {
    return mappings[`${roleId}_${permissionId}`] || false;
  };

  const saveChanges = async () => {
    try {
      setSaving(true);

      // Group changes by role
      const roleChanges = {};
      roles.forEach(role => {
        roleChanges[role.roleId] = [];
      });

      // Build permission arrays for each role
      Object.keys(mappings).forEach(key => {
        const [roleId, permissionId] = key.split('_');
        if (mappings[key]) {
          roleChanges[roleId].push(permissionId);
        }
      });

      // Save each role's permissions
      const extraBody = isPlatformOwner && targetCompanyId ? { targetCompanyId } : {};
      for (const role of roles) {
        await apiClient.put(`/roles/${role.roleId}/permissions`, {
          permissions: roleChanges[role.roleId],
          ...extraBody
        });
      }

      alert('Permissions updated successfully!');
      setHasChanges(false);
      setChangedMappings({});
      await fetchPermissionsMatrix();

    } catch (error) {
      console.error('Failed to save permissions:', error);
      alert('Failed to save permissions: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const cancelChanges = () => {
    if (hasChanges && !confirm('Discard unsaved changes?')) {
      return;
    }
    fetchPermissionsMatrix();
    setChangedMappings({});
    setHasChanges(false);
  };

  const createRole = async () => {
    if (!newRoleName.trim()) {
      alert('Role name is required');
      return;
    }

    try {
      setCreatingRole(true);
      await apiClient.post('/roles', {
        roleName: newRoleName.trim(),
        description: newRoleDescription.trim()
      });

      alert(`Role "${newRoleName}" created successfully!`);
      setShowAddRoleModal(false);
      setNewRoleName('');
      setNewRoleDescription('');
      await fetchPermissionsMatrix();

    } catch (error) {
      console.error('Failed to create role:', error);
      alert('Failed to create role: ' + (error.response?.data?.message || error.message));
    } finally {
      setCreatingRole(false);
    }
  };

  const deleteRole = async (roleId, roleName) => {
    if (!confirm(`Are you sure you want to delete the role "${roleName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await apiClient.delete(`/roles/${roleId}`);
      alert(`Role "${roleName}" deleted successfully!`);
      await fetchPermissionsMatrix();
    } catch (error) {
      console.error('Failed to delete role:', error);
      alert('Failed to delete role: ' + (error.response?.data?.message || error.message));
    }
  };

  const toggleSection = (section) =>
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));

  // ── Reorganise permissions from API into section/group structure ────────────
  const reorganizedSections = useMemo(() => {
    const allPerms = [];
    Object.values(permissions).forEach(catPerms => catPerms.forEach(p => allPerms.push(p)));

    const SECTION_ORDER = ['Administration', 'Operations', 'Management', 'Platform', 'Other'];
    const buckets = {};

    allPerms.forEach(p => {
      const meta    = PERMISSION_META[p.permissionName];
      const section = p.platformOnly ? 'Platform' : (meta?.section || 'Other');
      const group   = meta?.group    || (p.platformOnly ? 'Platform' : 'Other');
      if (!buckets[section]) buckets[section] = {};
      if (!buckets[section][group]) buckets[section][group] = [];
      buckets[section][group].push(p);
    });

    return SECTION_ORDER
      .filter(s => buckets[s])
      .map(s => ({
        section: s,
        config:  SECTION_CONFIG[s] || SECTION_CONFIG.Other,
        groups:  Object.entries(buckets[s]).map(([groupName, perms]) => ({
          name: groupName,
          perms: [...perms].sort((a, b) => {
            const oa = PERMISSION_META[a.permissionName]?.order ?? 999;
            const ob = PERMISSION_META[b.permissionName]?.order ?? 999;
            return oa - ob || a.permissionName.localeCompare(b.permissionName);
          })
        }))
      }));
  }, [permissions]);

  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return reorganizedSections;
    const q = searchQuery.toLowerCase();
    return reorganizedSections
      .map(sec => ({
        ...sec,
        groups: sec.groups
          .map(grp => ({
            ...grp,
            perms: grp.perms.filter(p => {
              const meta = PERMISSION_META[p.permissionName] || {};
              return (
                (meta.label || p.permissionName).toLowerCase().includes(q) ||
                (meta.description || p.description || '').toLowerCase().includes(q) ||
                p.permissionName.toLowerCase().includes(q)
              );
            })
          }))
          .filter(grp => grp.perms.length > 0)
      }))
      .filter(sec => sec.groups.length > 0);
  }, [reorganizedSections, searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading permissions…</div>
      </div>
    );
  }

  const totalPerms = reorganizedSections.reduce(
    (acc, s) => acc + s.groups.reduce((a, g) => a + g.perms.length, 0), 0
  );
  const matchCount = filteredSections.reduce(
    (acc, s) => acc + s.groups.reduce((a, g) => a + g.perms.length, 0), 0
  );

  return (
    <div className="space-y-4 pb-16">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Role Permissions</h1>
          <p className="mt-1 text-sm text-gray-500">
            Control what each role is allowed to do — {totalPerms} permissions across {roles.length} roles
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => setShowAddRoleModal(true)} className="btn btn-success text-sm">
            + Add Role
          </button>
          <button onClick={() => navigate('/admin')} className="btn btn-secondary text-sm">
            ← Back
          </button>
        </div>
      </div>

      {/* Platform owner: company selector */}
      {isPlatformOwner && companies.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-4">
          <span className="text-sm font-semibold text-purple-800">👑 Platform Admin — Editing company:</span>
          <select
            value={targetCompanyId || ''}
            onChange={(e) => handleCompanyChange(e.target.value)}
            className="border border-purple-300 rounded-md px-3 py-1.5 text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {companies.map(c => (
              <option key={c.CompanyID} value={c.CompanyID}>{c.CompanyName}</option>
            ))}
          </select>
          <span className="text-xs text-purple-600">Permissions shown are specific to this company's roles.</span>
        </div>
      )}

      {/* ── Unsaved-changes banner ──────────────────────────────────────────── */}
      {hasChanges && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 flex items-center justify-between">
          <p className="text-sm font-medium text-yellow-800">⚠️ You have unsaved changes — highlighted checkboxes are pending</p>
          <div className="flex gap-2">
            <button onClick={cancelChanges} className="btn btn-secondary text-sm">Cancel</button>
            <button onClick={saveChanges} disabled={saving} className="btn btn-primary text-sm">
              {saving ? 'Saving…' : '💾 Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* ── Search + Legend ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search permissions…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xl leading-none"
            >×</button>
          )}
        </div>
        {searchQuery && (
          <span className="text-sm text-gray-400 shrink-0">{matchCount} of {totalPerms} shown</span>
        )}
        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-blue-200 border border-blue-400" /><span>Administration</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-emerald-200 border border-emerald-400" /><span>Operations</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-purple-200 border border-purple-400" /><span>Management</span></div>
          <div className="flex items-center gap-2 pl-3 border-l border-gray-300">
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-500 font-mono border border-gray-300">≈ scoped</span>
            <span>Department-scoped permission</span>
          </div>
        </div>
      </div>

      {/* ── Permissions Matrix ─────────────────────────────────────────────── */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-white border-b-2 border-gray-200">
                <th className="sticky left-0 z-20 bg-white px-5 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-[280px] w-[280px] shadow-[2px_0_4px_rgba(0,0,0,0.06)]">
                  Permission
                </th>
                {roles.map(role => {
                  const meta = ROLE_META[role.roleName] || { desc: role.description || '', badge: 'bg-gray-100 text-gray-700 border-gray-200', accent: 'bg-violet-400' };
                  const isCustom = !SYSTEM_ROLE_NAMES.has(role.roleName);
                  return (
                    <th key={role.roleId} className="text-center min-w-[120px] border-r border-gray-200 last:border-r-0 align-top p-0">
                      <div className={`h-1.5 ${meta.accent || 'bg-gray-400'}`} />
                      <div className="px-3 py-3 flex flex-col items-center gap-1.5">
                        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${meta.badge}`}>
                          {role.roleName}
                        </span>
                        <span className="text-[10px] text-gray-400 font-normal normal-case text-center leading-tight max-w-[95px]">
                          {meta.desc || role.description || ''}
                        </span>
                        {isCustom && (
                          <button
                            onClick={() => deleteRole(role.roleId, role.roleName)}
                            className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] text-red-400 hover:text-red-600 transition-colors"
                            title="Delete this custom role"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {filteredSections.map(({ section, config, groups }) => (
                <Fragment key={section}>
                  {/* ── Section banner ──────────────────────────────────── */}
                  <tr
                    className={`${config.headerBg} cursor-pointer select-none`}
                    onClick={() => toggleSection(section)}
                  >
                    <td colSpan={roles.length + 1} className="px-5 py-2.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`text-sm font-bold uppercase tracking-wider ${config.headerText}`}>
                            {config.label}
                          </span>
                          <span className={`text-xs hidden sm:inline ${config.headerMuted}`}>— {config.description}</span>
                          <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold ${config.headerBadge}`}>
                            {groups.reduce((a, g) => a + g.perms.length, 0)}
                          </span>
                        </div>
                        <svg
                          className={`w-4 h-4 transition-transform duration-150 ${config.headerChevron} ${collapsedSections[section] ? '-rotate-90' : ''}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </td>
                  </tr>

                  {!collapsedSections[section] && groups.map(({ name: groupName, perms }) => (
                    <Fragment key={`${section}-${groupName}`}>
                      {/* ── Group header ──────────────────────────────── */}
                      <tr className={`${config.groupBg} border-t ${config.groupBorder}`}>
                        <td colSpan={roles.length + 1} className={`px-6 py-1.5 ${config.groupText}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wide">{groupName}</span>
                            <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-white/70 border ${config.groupBorder}`}>
                              {perms.length}
                            </span>
                          </div>
                        </td>
                      </tr>

                      {/* ── Permission rows ────────────────────────────── */}
                      {perms.map((permission, rowIdx) => {
                        const meta = PERMISSION_META[permission.permissionName] || {};
                        return (
                          <tr
                            key={permission.permissionId}
                            className={`${rowIdx % 2 === 1 ? 'bg-gray-50/70' : 'bg-white'} ${config.rowHover} border-b border-gray-100 transition-colors`}
                          >
                            <td className="sticky left-0 z-10 bg-white px-5 py-3 border-r border-gray-100 shadow-[2px_0_4px_rgba(0,0,0,0.05)]">
                              <div className="min-w-0">
                                <div className="flex items-center flex-wrap gap-1.5">
                                  <span className="text-sm font-medium text-gray-900" title={permission.permissionName}>
                                    {meta.label || permission.permissionName}
                                  </span>
                                  {meta.note && (
                                    <span
                                      className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono border border-gray-200 cursor-help"
                                      title={meta.note}
                                    >
                                      ≈ scoped
                                    </span>
                                  )}
                                </div>
                                {(meta.description || permission.description) && (
                                  <p className="text-xs text-gray-400 mt-0.5 leading-snug">
                                    {meta.description || permission.description}
                                  </p>
                                )}
                              </div>
                            </td>
                            {roles.map(role => {
                              const key       = `${role.roleId}_${permission.permissionId}`;
                              const isChecked = roleHasPermission(role.roleId, permission.permissionId);
                              const isChanged = changedMappings[key] !== undefined;
                              return (
                                <td
                                  key={key}
                                  className={`px-3 py-3 text-center border-r border-gray-100 last:border-r-0 transition-colors ${isChecked ? 'bg-emerald-50/50' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => togglePermission(role.roleId, permission.permissionId)}
                                    className={`h-4 w-4 rounded border-gray-300 cursor-pointer ${config.checkColor} ${
                                      isChanged ? 'ring-2 ring-yellow-400 ring-offset-1' : ''
                                    }`}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </Fragment>
                  ))}
                </Fragment>
              ))}

              {/* No-results row */}
              {filteredSections.length === 0 && (
                <tr>
                  <td colSpan={roles.length + 1} className="py-16 text-center">
                    <p className="text-sm text-gray-400">No permissions match "<span className="font-medium text-gray-600">{searchQuery}</span>"</p>
                    <button onClick={() => setSearchQuery('')} className="mt-2 text-sm text-blue-500 hover:underline">Clear search</button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Sticky save bar ─────────────────────────────────────────────────── */}
      {hasChanges && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-4">
          <div className="bg-white border border-yellow-300 shadow-2xl rounded-xl px-5 py-3 flex items-center justify-between gap-4">
            <p className="text-sm font-medium text-yellow-800">⚠️ Unsaved changes</p>
            <div className="flex gap-2">
              <button onClick={cancelChanges} className="btn btn-secondary text-sm">Cancel</button>
              <button onClick={saveChanges} disabled={saving} className="btn btn-primary text-sm">
                {saving ? 'Saving…' : '💾 Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Help panel ──────────────────────────────────────────────────────── */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">How permissions work</h3>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>Tick a checkbox to grant a permission to a role — untick to remove it</li>
          <li>Changes are highlighted with a yellow ring until saved</li>
          <li>Users receive updated permissions immediately after saving</li>
          <li>Permissions marked <strong>≈ scoped</strong> are department-restricted versions, typically assigned to Department Managers</li>
          <li>Custom roles can be deleted if not assigned to any active users</li>
        </ul>
      </div>

      {/* ── Add Role Modal ──────────────────────────────────────────────────── */}
      {showAddRoleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Role</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role Name *</label>
                <input
                  type="text"
                  value={newRoleName}
                  onChange={e => setNewRoleName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Accountant, Auditor"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newRoleDescription}
                  onChange={e => setNewRoleDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe what this role is allowed to do…"
                  rows={3}
                  maxLength={200}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowAddRoleModal(false); setNewRoleName(''); setNewRoleDescription(''); }}
                className="btn btn-secondary flex-1"
                disabled={creatingRole}
              >
                Cancel
              </button>
              <button
                onClick={createRole}
                disabled={creatingRole || !newRoleName.trim()}
                className="btn btn-success flex-1"
              >
                {creatingRole ? 'Creating…' : 'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

